import React, { useContext, ReactElement } from 'react';
import styled, { css, keyframes, ThemeContext } from 'styled-components';
import colors from '../common/theme/colors';
import { TranslateFunc } from '../common/types';
import constants from '../common/theme/constants';

const { spacing, zIndex } = constants;

// - SIZES, BORDERS ARE STILL ARE TBD --
const sizes: Record<string, number> = {
  s: 30,
  m: 60,
  l: 90,
};

const borders: Record<string, number> = {
  s: 4,
  m: 6,
  l: 10,
};

const speeds: Record<string, string> = {
  default: '0.6s',
  fast: '0.4s',
  faster: '0.2s',
};

type align = 'top' | 'middle';

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  height: 100%;
  padding: ${spacing.absolute.l}px;
  background-color: rgba(255, 255, 255, 0.5);
  z-index: ${zIndex.loader};
`;

export interface KitSpinnerProps {
  color?: 'default' | 'gray';
  size?: 's' | 'm' | 'l';
  align?: align;
  speed?: 'default' | 'fast' | 'faster';
  ariaLabel?: string;
  withOverlay?: boolean;
  noDelay?: boolean;
  show?: boolean;
  t?: TranslateFunc;
}

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const SpinnerContainer = styled.div<{
  border: number;
  size: number;
  color: string;
  speed: string;
  noDelay: boolean;
  align: align;
  withOverlay: boolean;
}>`
  border: ${({ border }) => border}px solid ${colors.gray_20};
  border-top: ${({ border }) => border}px solid ${({ color }) => color};
  border-right: ${({ border }) => border}px solid ${({ color }) => color};
  border-bottom: ${({ border }) => border}px solid ${({ color }) => color};
  border-radius: 50%;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  margin: auto;
  animation: ${spin} ${({ speed }) => speed} linear infinite;
  animation-delay: ${({ noDelay }) => (noDelay ? '0' : '0.5s')};
  ${({ withOverlay, size }) =>
    withOverlay &&
    css`
      position: absolute;
      top: calc(50% - ${size / 2}px);
    `}
  ${({ align }) =>
    align &&
    align === 'top' &&
    css`
      top: 10%;
    `}
`;

const SpinnerAlignCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto auto;
`;

const Container = ({
  align,
  ariaLabelText,
  border,
  borderColor,
  noDelay,
  speed,
  width,
  withOverlay,
}) => (
  <SpinnerAlignCenter>
    <SpinnerContainer
      speed={speed}
      size={width}
      border={border}
      color={borderColor}
      aria-label={ariaLabelText}
      aria-valuetext={ariaLabelText}
      aria-live='polite'
      role='progressbar'
      noDelay={noDelay}
      align={align}
      withOverlay={withOverlay}
    />
  </SpinnerAlignCenter>
);

const KitSpinner = ({
  size = 'm',
  color = 'default',
  align,
  speed = 'default',
  ariaLabel,
  withOverlay,
  noDelay = false,
  show = true,
  t,
}: KitSpinnerProps): ReactElement => {
  const themeContext = useContext(ThemeContext);

  let borderColor;
  if (themeContext.spinner) {
    borderColor = themeContext.spinner.color;
  } else {
    borderColor = color === 'default' ? colors.orange_50 : colors.gray_40;
  }

  const width = sizes[size];
  const border = borders[size];
  let ariaLabelText = 'Loading...';
  if (ariaLabel) {
    ariaLabelText = ariaLabel;
  } else if (t) {
    ariaLabelText = t('kit:spinner.loading');
  }

  if (!show) {
    return null;
  }

  const containerProps = {
    align,
    ariaLabelText,
    border,
    borderColor,
    noDelay,
    speed: speeds[speed],
    width,
    withOverlay,
  };

  return withOverlay ? (
    <Overlay>
      <Container {...containerProps} />
    </Overlay>
  ) : (
    <Container {...containerProps} />
  );
};

export default KitSpinner;
